import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Trash2, ChevronRight } from "lucide-react";
import { fmt } from "@/lib/api";

export default function DeviceList({ devices, onLink, onRemove, onFocus }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const list = devices.filter((d) => (filter === "all" || d.status === filter) && `${d.client_name} ${d.serial} ${d.city}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="kl-panel h-full flex flex-col text-kl-black" data-testid="device-list-panel">
      <div className="p-4 border-b border-kl-lineLight flex items-center justify-between">
        <div>
          <p className="kl-label">Linked nodes</p>
          <h2 className="font-display text-lg mt-0.5">Devices <span className="font-mono text-sm text-kl-mutedLight">{devices.length}</span></h2>
        </div>
        <button data-testid="link-device-button" onClick={onLink} className="kl-btn-primary h-9 px-4 text-xs"><Plus size={14} /> Register device</button>
      </div>
      <div className="p-3 border-b border-kl-lineLight flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kl-mutedLight" />
          <input data-testid="device-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Device" className="kl-input-box pl-9 h-9" />
        </div>
        <div className="flex border border-kl-lineLight">
          {["all", "active", "inactive"].map((f) => (
            <button key={f} data-testid={`device-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 text-[11px] font-mono uppercase tracking-wider transition-colors duration-150 ${filter === f ? "bg-kl-black text-white" : "text-kl-mutedLight hover:bg-kl-light"}`}>{f}</button>
          ))}
        </div>
      </div>
      <ul className="flex-1 overflow-y-auto kl-scroll-light">
        {list.length === 0 && <li className="p-6 text-sm text-kl-mutedLight text-center" data-testid="device-list-empty">{devices.length === 0 ? "No devices linked in this market yet. Link a node to get started." : "No devices match."}</li>}
        {list.map((d) => (
          <li key={d.id} className="kl-row group" data-testid={`device-row-${d.serial}`}>
            <div className="flex items-stretch">
              <button onClick={() => onFocus(d)} onDoubleClick={() => nav(`/devices/${d.id}`)} className="flex-1 text-left p-3 flex gap-3 items-center kl-focus" data-testid={`device-focus-${d.serial}`}>
                <span className={`w-2 h-2 shrink-0 ${d.status === "active" ? "bg-kl-active" : "bg-kl-inactive"}`} data-testid={`device-status-${d.serial}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-display text-sm truncate">{d.client_name}</span>
                    <span className="font-mono text-xs shrink-0">{d.live && d.status === "active" ? `${(d.live.power_w / 1000).toFixed(2)} kW` : "—"}</span>
                  </div>
                  <div className="flex justify-between gap-2 mt-0.5">
                    <span className="font-mono text-[11px] text-kl-mutedLight truncate">{d.serial}</span>
                    <span className="text-[11px] text-kl-mutedLight shrink-0" title="Last time the board pushed a reading">{d.city} · <span className="font-mono">{d.region_code}</span> · synced {fmt.ago(d.last_seen)}</span>
                  </div>
                </div>
              </button>
              <div className="flex flex-col border-l border-kl-lineLight opacity-60 group-hover:opacity-100 transition-opacity duration-150">
                <button title="Open dashboard" data-testid={`device-open-${d.serial}`} onClick={() => nav(`/devices/${d.id}`)} className="flex-1 px-2 hover:bg-kl-black hover:text-white transition-colors duration-150"><ChevronRight size={14} /></button>
                <button title="Delete device" data-testid={`device-remove-${d.serial}`} onClick={() => onRemove(d)} className="flex-1 px-2 border-t border-kl-lineLight hover:bg-kl-inactive hover:text-white transition-colors duration-150"><Trash2 size={13} /></button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
